import React, { useEffect, useState } from "react";
import { Box, Button, Grid, Step, StepLabel, Stepper, Typography } from "@mui/material";
import SwipeableViews from "react-swipeable-views";
import { useTheme } from "@mui/material/styles";
import { TabPanel } from "../../../../../utils/cssStyles";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { MENU_OPEN, SET_BREADCRUMBS } from "../../../../../redux/actions/actions";
import * as Yup from "yup";
import { IMAGE_SIZE, IMAGE_SUPPORTED_FORMATS } from "../../../../../store/constants";
import { Form, Formik } from "formik";
import UserDetails from "./user-details";
import OperatorConfiguration from "./user-operator-configuration";
import { UserService } from "../../../../../assets/_services/user-service";
import { openSuccessDialog } from "../../../../../utils/ui-components/pop-ups/SuccessDialog";
import { convertFileToBase64 } from "../../../../../utils/utils";
import { RateCardService } from "../../../../../assets/_services/rate-card-service";

export interface UserAddValues {
  profileImage: object;
  representativeName: string;
  email: string;
  nid: string;
  vat: string;
  companyName: string;
  businessName: string;
  companyContact: string;
  mobile: string;
  companyLocation: string;
  industryType: string;
  accountType: string;
  username: string;
  password: string;
  confirmPassword: string;
  status: boolean;
  isShowRateCard: boolean;
  enableUserCreation: boolean;
  enableApiCreation: boolean;
  enableReportDownload: boolean;
  isCampaignNumberHash: boolean;
  enableRewardCampaignCreation: boolean;
  rateCardId: number | string;
  dataRateCardId: number | string;
  smsOperatorConfguraion: {
    all: boolean;
    defaultOperator: string | number;
    robi: {
      isUse: boolean;
      systemOperatorId: number;
      userName: string;
      password: string;
      tps: string;
    };
    tl: {
      isUse: boolean;
      systemOperatorId: number;
      userName: string;
      password: string;
      tps: string;
      acode: string;
    };
    bl: {
      isUse: boolean;
      systemOperatorId: number;
      userName: string;
      password: string;
      tps: string;
      billmsisdn: string;
    };
    gp: {
      isUse: boolean;
      systemOperatorId: number;
      userName: string;
      password: string;
      tps: string;
      billmsisdn: string;
    };
  };
  rewardOperatorConfguraion: {
    all: boolean;
    robi: {
      isUse: boolean;
      systemOperatorId: number;
      userName: string;
      password: string;
    };
    tl: {
      isUse: boolean;
      systemOperatorId: number;
      userName: string;
      password: string;
    };
    bl: {
      isUse: boolean;
      systemOperatorId: number;
      userName: string;
      password: string;
      clientId: string;
      grantType: string;
      scope: string;
      clientSecret: string;
      senderMsisdn: string;
    };
    gp: {
      isUse: boolean;
      systemOperatorId: number;
      clientId: string;
      clientSecret: string;
      grantType: string;
    };
  };
}

const INITIAL_FORM_STATE: UserAddValues = {
  profileImage: {},
  representativeName: "",
  email: "",
  nid: "",
  vat: "",
  companyName: "",
  businessName: "",
  companyContact: "",
  mobile: "",
  companyLocation: "",
  industryType: "",
  accountType: "",
  username: "",
  password: "",
  confirmPassword: "",
  rateCardId: "",
  dataRateCardId: "",
  status: true,
  enableUserCreation: true,
  enableApiCreation: true,
  isShowRateCard: true,
  enableReportDownload: true,
  isCampaignNumberHash: true,
  enableRewardCampaignCreation: true,
  smsOperatorConfguraion: {
    all: true,
    defaultOperator: "",
    robi: {
      isUse: true,
      systemOperatorId: 1,
      userName: "",
      password: "",
      tps: "",
    },
    tl: {
      isUse: true,
      systemOperatorId: 2,
      userName: "",
      password: "",
      tps: "",
      acode: "",
    },
    bl: {
      isUse: true,
      systemOperatorId: 3,
      userName: "",
      password: "",
      tps: "",
      billmsisdn: "",
    },
    gp: {
      isUse: true,
      systemOperatorId: 4,
      userName: "",
      password: "",
      tps: "",
      billmsisdn: "",
    },
  },
  rewardOperatorConfguraion: {
    all: true,
    robi: {
      isUse: true,
      systemOperatorId: 1,
      userName: "",
      password: "",
    },
    tl: {
      isUse: true,
      systemOperatorId: 2,
      userName: "",
      password: "",
    },
    bl: {
      isUse: true,
      systemOperatorId: 3,
      userName: "",
      password: "",
      clientId: "",
      grantType: "",
      scope: "",
      clientSecret: "",
      senderMsisdn: "",
    },
    gp: {
      isUse: true,
      systemOperatorId: 4,
      clientId: "",
      clientSecret: "",
      grantType: "",
    },
  },
};

const FORM_VALIDATION = Yup.object().shape({
  profileImage: Yup.mixed()
    .nullable()
    .notRequired()
    .test("FILE_SIZE", "Uploaded file is too big.", ({ file: value }: any) => !value || (value && value?.size <= IMAGE_SIZE))
    .test(
      "FILE_FORMAT",
      "Uploaded file has unsupported format.",
      ({ file: value }: any) => !value || (value && IMAGE_SUPPORTED_FORMATS.includes(`${value?.type},`)),
    ),
  representativeName: Yup.string().required("Representative name is required").trim("Enter valid name"),
  email: Yup.string().email("Must be a valid email").max(255).required("Email is required"),
  nid: Yup.string().required("NID number is required"),
  vat: Yup.string().required("VAT number is required"),
  companyName: Yup.string().required("Company name is required").trim("Enter valid name"),
  businessName: Yup.string().required("Business name is required").trim("Enter valid name"),
  companyContact: Yup.string()
    .matches(/^((880|0)?(1){1}[3456789]{1}(\d){8},?)+$/, "Invalid mobile numbers pattern detected")
    .required("Please enter company contact number"),
  mobile: Yup.string()
    .matches(/^((880|0)?(1){1}[3456789]{1}(\d){8},?)+$/, "Invalid mobile numbers pattern detected")
    .required("Mobile number is required"),
  companyLocation: Yup.string().max(255, "maximum character count exceeded").required("Company location is required").trim("Enter valid location"),
  username: Yup.string()
    .required("Username is required")
    .matches(/^[a-zA-Z0-9]*$/, "Contains only letters and numbers"),
  password: Yup.string()
    .max(255)
    .required("Password is required")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/,
      "Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character",
    ),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password")], "Your passwords do not match.")
    .notRequired(),
  industryType: Yup.number().required("You must make a selection"),
  accountType: Yup.number().required("You must make a selection"),
  rateCardId: Yup.string().required("You must select a sms rate card"),
  dataRateCardId: Yup.string().when("enableRewardCampaignCreation", {
    is: (val: any) => val,
    then: (schema: any) => schema.required("Select data reward rate card"),
    otherwise: (schema: any) => schema.notRequired(),
  }),
  status: Yup.boolean().notRequired(),
  isShowRateCard: Yup.boolean().notRequired(),
  enableUserCreation: Yup.boolean().notRequired(),
  enableApiCreation: Yup.boolean().notRequired(),
  enableReportDownload: Yup.boolean().notRequired(),
  isCampaignNumberHash: Yup.boolean().notRequired(),
  enableRewardCampaignCreation: Yup.boolean().notRequired(),
  smsOperatorConfguraion: Yup.object()
    .shape({
      defaultOperator: Yup.string().required("Select default operator"),
      all: Yup.boolean().notRequired(),
      robi: Yup.object().shape({
        isUse: Yup.boolean().notRequired(),
        userName: Yup.string()
          // .matches(/^[a-zA-Z0-9]*$/, "Contains only letters and numbers")
          .when("isUse", {
            is: (val: any) => val,
            then: (schema: any) => schema.required("Robi username required"),
            otherwise: (schema: any) => schema.notRequired(),
          }),
        tps: Yup.number()
          .when("isUse", {
            is: (val: any) => val,
            then: (schema: any) => schema.required("Robi TPS required"),
            otherwise: (schema: any) => schema.notRequired(),
          })
          .typeError("Allow only numbers")
          .integer("TPS must be an integer")
          .min(1, "TPS must be greater than or equal to 0"),
        password: Yup.string()
          .max(255)
          .when("isUse", {
            is: (val: any) => val,
            then: (schema: any) => schema.required("Robi password required"),
            otherwise: (schema: any) => schema.notRequired(),
          }),
      }),
      tl: Yup.object().shape({
        isUse: Yup.boolean().notRequired(),
        userName: Yup.string().when("isUse", {
          is: (val: any) => val,
          then: (schema: any) => schema.required("Teletalk username required"),
          otherwise: (schema: any) => schema.notRequired(),
        }),
        // .matches(/^[a-zA-Z0-9]*$/, "Contains only letters and numbers"),
        tps: Yup.number()
          .when("isUse", {
            is: (val: any) => val,
            then: (schema: any) => schema.required("Teletalk TPS required"),
            otherwise: (schema: any) => schema.notRequired(),
          })
          .typeError("Allow only numbers")
          .integer("TPS must be an integer")
          .min(1, "TPS must be greater than or equal to 0"),
        acode: Yup.string().when("isUse", {
          is: (val: any) => val,
          then: (schema: any) => schema.required("Teletalk acode required"),
          otherwise: (schema: any) => schema.notRequired(),
        }),
        password: Yup.string()
          .max(255)
          .when("isUse", {
            is: (val: any) => val,
            then: (schema: any) => schema.required("Teletalk password required"),
            otherwise: (schema: any) => schema.notRequired(),
          }),
      }),
      bl: Yup.object().shape({
        isUse: Yup.boolean().notRequired(),
        userName: Yup.string().when("isUse", {
          is: (val: any) => val,
          then: (schema: any) => schema.required("Banglalink username required"),
          otherwise: (schema: any) => schema.notRequired(),
        }),
        // .matches(/^[a-zA-Z0-9]*$/, "Contains only letters and numbers"),
        tps: Yup.number()
          .when("isUse", {
            is: (val: any) => val,
            then: (schema: any) => schema.required("Banglalink TPS required"),
            otherwise: (schema: any) => schema.notRequired(),
          })
          .typeError("Allow only numbers")
          .integer("TPS must be an integer")
          .min(1, "TPS must be greater than or equal to 0"),
        password: Yup.string()
          .max(255)
          .when("isUse", {
            is: (val: any) => val,
            then: (schema: any) => schema.required("Banglalink password required"),
            otherwise: (schema: any) => schema.notRequired(),
          }),
        billmsisdn: Yup.string().when("isUse", {
          is: (val: any) => val,
          then: (schema: any) =>
            schema.matches(/^((880)(\d){10},?)+$/, "Invalid numbers pattern detected").required("Banglalink billmsisdn required"),
          otherwise: (schema: any) => schema.notRequired(),
        }),
      }),
      gp: Yup.object().shape({
        isUse: Yup.boolean().notRequired(),
        userName: Yup.string().when("isUse", {
          is: (val: any) => val,
          then: (schema: any) => schema.required("Grameenphone username required"),
          otherwise: (schema: any) => schema.notRequired(),
        }),
        // .matches(/^[a-zA-Z0-9]*$/, "Contains only letters and numbers"),
        tps: Yup.number()
          .when("isUse", {
            is: (val: any) => val,
            then: (schema: any) => schema.required("Grameenphone TPS required"),
            otherwise: (schema: any) => schema.notRequired(),
          })
          .typeError("Allow only numbers")
          .integer("TPS must be an integer")
          .min(1, "TPS must be greater than or equal to 0"),
        password: Yup.string()
          .max(255)
          .when("isUse", {
            is: (val: any) => val,
            then: (schema: any) => schema.required("Grameenphone password required"),
            otherwise: (schema: any) => schema.notRequired(),
          }),
        billmsisdn: Yup.string().when("isUse", {
          is: (val: any) => val,
          then: (schema: any) =>
            schema.matches(/^((0)?(\d){10},?)+$/, "Invalid numbers pattern detected").required("Grameenphone billmsisdn required"),
          otherwise: (schema: any) => schema.notRequired(),
        }),
      }),
    })
    .test("valid", "valid", function (this: Yup.TestContext) {
      const { smsOperatorConfguraion } = this.parent;
      const { robi, tl, bl, gp } = smsOperatorConfguraion;
      const isAnyUseTrue = [robi, tl, bl, gp].some((item) => item.isUse === true);
      return isAnyUseTrue;
    }),
  rewardOperatorConfguraion: Yup.object().when("enableRewardCampaignCreation", {
    is: (val: any) => val,
    then: (schema: any) =>
      schema
        .shape({
          all: Yup.boolean().required("Required"),
          robi: Yup.object().shape({
            isUse: Yup.boolean().required("Required"),
            userName: Yup.string().when("isUse", {
              is: (val: any) => val,
              then: (schema: any) => schema.required("Username is required"),
              otherwise: (schema: any) => schema.notRequired(),
            }),
            // .matches(/^[a-zA-Z0-9]*$/, "Contains only letters and numbers"),
            password: Yup.string()
              .max(255)
              .when("isUse", {
                is: (val: any) => val,
                then: (schema: any) => schema.required("Password is required"),
                otherwise: (schema: any) => schema.notRequired(),
              }),
          }),
          tl: Yup.object().shape({
            isUse: Yup.boolean().required("Required"),
            userName: Yup.string().when("isUse", {
              is: (val: any) => val,
              then: (schema: any) => schema.required("Username is required"),
              otherwise: (schema: any) => schema.notRequired(),
            }),
            // .matches(/^[a-zA-Z0-9]*$/, "Contains only letters and numbers"),
            password: Yup.string()
              .max(255)
              .when("isUse", {
                is: (val: any) => val,
                then: (schema: any) => schema.required("Password is required"),
                otherwise: (schema: any) => schema.notRequired(),
              }),
          }),
          bl: Yup.object().shape({
            isUse: Yup.boolean().required("Required"),
            userName: Yup.string().when("isUse", {
              is: (val: any) => val,
              then: (schema: any) => schema.required("Username is required"),
              otherwise: (schema: any) => schema.notRequired(),
            }),
            // .matches(/^[a-zA-Z0-9]*$/, "Contains only letters and numbers"),
            password: Yup.string()
              .max(255)
              .when("isUse", {
                is: (val: any) => val,
                then: (schema: any) => schema.required("Password is required"),
                otherwise: (schema: any) => schema.notRequired(),
              }),
            clientId: Yup.string().when("isUse", {
              is: (val: any) => val,
              then: (schema: any) => schema.required("Client ID is required"),
              otherwise: (schema: any) => schema.notRequired(),
            }),
            grantType: Yup.string().when("isUse", {
              is: (val: any) => val,
              then: (schema: any) => schema.required("Grant type is required"),
              otherwise: (schema: any) => schema.notRequired(),
            }),
            scope: Yup.string().when("isUse", {
              is: (val: any) => val,
              then: (schema: any) => schema.required("Scope is required"),
              otherwise: (schema: any) => schema.notRequired(),
            }),
            clientSecret: Yup.string().when("isUse", {
              is: (val: any) => val,
              then: (schema: any) => schema.required("Client secret is required"),
              otherwise: (schema: any) => schema.notRequired(),
            }),
            senderMsisdn: Yup.string()
              .matches(/^((880|0)?(1){1}[3456789]{1}(\d){8},?)+$/, "Invalid mobile numbers pattern detected")
              .when("isUse", {
                is: (val: any) => val,
                then: (schema: any) => schema.required("Sender Msisdn is required"),
                otherwise: (schema: any) => schema.notRequired(),
              }),
          }),
          gp: Yup.object().shape({
            isUse: Yup.boolean().required("Required"),
            clientId: Yup.string().when("isUse", {
              is: (val: any) => val,
              then: (schema: any) => schema.required("Client ID is required"),
              otherwise: (schema: any) => schema.notRequired(),
            }),
            grantType: Yup.string().when("isUse", {
              is: (val: any) => val,
              then: (schema: any) => schema.required("Grant type is required"),
              otherwise: (schema: any) => schema.notRequired(),
            }),
            clientSecret: Yup.string().when("isUse", {
              is: (val: any) => val,
              then: (schema: any) => schema.required("Client secret is required"),
              otherwise: (schema: any) => schema.notRequired(),
            }),
          }),
        })
        .test("valid", "valid", function (this: Yup.TestContext) {
          const { rewardOperatorConfguraion } = this.parent;
          const { robi, tl, bl, gp } = rewardOperatorConfguraion;
          const isAnyUseTrue = [robi, tl, bl, gp].some((item) => item.isUse === true);
          return isAnyUseTrue;
        }),
    otherwise: (schema: any) => schema.notRequired(),
  }),
});

function CreateParentUser() {
  const theme: any = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [steps, setSteps] = useState(["User Details", "Operator Configuration"]);
  const [activeStep, setActiveStep] = useState(0);
  const [activateNextButton, setActivateNextButton] = useState(false);
  const path = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [smsRateCards, setSmsRateCards] = useState<{ value: string | number; label: string }[]>([]);
  const [dataRateCards, setDataRateCards] = useState<{ value: string | number; label: string }[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const id = "/config/users";
    dispatch({ type: MENU_OPEN, id });
    dispatch({
      type: SET_BREADCRUMBS,
      payload: [
        { name: "Home", path: "/Dashboard", bold: true },
        { name: "Users", path: "/config/users", bold: true },
        { name: "Creation", path: null, bold: false },
      ],
    });

    RateCardService.getRateCardListWithoutPagination().then((response) => {
      if (response.isSuccess) {
        setSmsRateCards(response.data.data.map((item: any) => ({ value: item.id, label: item.rateCardName })));
      }
    });
    RateCardService.getDigitalRateCardListWithoutPagination().then((response) => {
      if (response.isSuccess) {
        setDataRateCards(response.data.data.map((item: any) => ({ value: item.id, label: item.rateCardName })));
      }
    });
  }, []);

  const handleNext = () => {
    setActivateNextButton(false);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActivateNextButton(true);
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  const createUser = (itemData: UserAddValues, setSubmitting: (isSubmitting: boolean) => void) => {
    setIsLoading(true);
    let data: any;
    convertFileToBase64(itemData.profileImage)
      .then((base64) => {
        data = { ...itemData, profileImage: base64 };
      })
      .catch((error) => {
        data = { ...itemData, profileImage: "" };
      })
      .finally(() => {
        let formData = {
          companyName: itemData.companyName,
          businessName: itemData.businessName,
          companyContact: itemData.companyContact,
          companyLocation: itemData.companyLocation,
          industryType: itemData.industryType,
          accountType: Number(itemData.accountType) - 1,
          NID: itemData.nid,
          VAT: itemData.vat,
          representativeName: itemData.representativeName,
          email: itemData.email,
          userName: itemData.username,
          password: itemData.password,
          rateCardId: itemData.rateCardId,
          status: itemData.status,
          image: data.profileImage,
          mobile: itemData?.mobile,
          isRateCardShow: itemData?.isShowRateCard,
          isAbleToCreateChildUser: itemData?.enableUserCreation,
          isApiUser: itemData?.enableApiCreation,
          isNumberListHashed: itemData?.isCampaignNumberHash,
          isReportDownload: itemData?.enableReportDownload,
          isDataRewardAvailable: itemData?.enableRewardCampaignCreation,
          dataRateCardId: itemData?.enableRewardCampaignCreation ? itemData?.dataRateCardId : 0,
          isAllOperatorsAllowed: itemData?.smsOperatorConfguraion.all,
          isAllDataOperatorAllowed: itemData?.rewardOperatorConfguraion.all,
          defaultOperatorId: itemData?.smsOperatorConfguraion.defaultOperator,
          systemOperatorConfigurations: [
            itemData.smsOperatorConfguraion.robi.isUse && {
              systemOperatorId: itemData.smsOperatorConfguraion.robi.systemOperatorId,
              userName: itemData.smsOperatorConfguraion.robi.userName,
              password: itemData.smsOperatorConfguraion.robi.password,
              tps: itemData.smsOperatorConfguraion.robi.tps,
            },
            itemData.smsOperatorConfguraion.tl.isUse && {
              systemOperatorId: itemData.smsOperatorConfguraion.tl.systemOperatorId,
              userName: itemData.smsOperatorConfguraion.tl.userName,
              password: itemData.smsOperatorConfguraion.tl.password,
              acode: itemData.smsOperatorConfguraion.tl.acode,
              tps: itemData.smsOperatorConfguraion.tl.tps,
            },
            itemData.smsOperatorConfguraion.bl.isUse && {
              systemOperatorId: itemData.smsOperatorConfguraion.bl.systemOperatorId,
              userName: itemData.smsOperatorConfguraion.bl.userName,
              password: itemData.smsOperatorConfguraion.bl.password,
              tps: itemData.smsOperatorConfguraion.bl.tps,
              billmsisdn: itemData.smsOperatorConfguraion.bl.billmsisdn,
            },
            itemData.smsOperatorConfguraion.gp.isUse && {
              systemOperatorId: itemData.smsOperatorConfguraion.gp.systemOperatorId,
              userName: itemData.smsOperatorConfguraion.gp.userName,
              password: itemData.smsOperatorConfguraion.gp.password,
              tps: itemData.smsOperatorConfguraion.gp.tps,
              billmsisdn: itemData.smsOperatorConfguraion.gp.billmsisdn,
            },
          ].filter((item) => typeof item !== "boolean"),
          dataSystemOperatorConfigurations: itemData?.enableRewardCampaignCreation
            ? [
                itemData?.rewardOperatorConfguraion.robi.isUse && {
                  systemOperatorId: itemData?.rewardOperatorConfguraion.robi.systemOperatorId,
                  userName: itemData?.rewardOperatorConfguraion.robi.userName,
                  password: itemData?.rewardOperatorConfguraion.robi.password,
                },
                itemData?.rewardOperatorConfguraion.tl.isUse && {
                  systemOperatorId: itemData?.rewardOperatorConfguraion.tl.systemOperatorId,
                  userName: itemData?.rewardOperatorConfguraion.tl.userName,
                  password: itemData?.rewardOperatorConfguraion.tl.password,
                },
                itemData?.rewardOperatorConfguraion.bl.isUse && {
                  systemOperatorId: itemData?.rewardOperatorConfguraion.bl.systemOperatorId,
                  userName: itemData?.rewardOperatorConfguraion.bl.userName,
                  password: itemData?.rewardOperatorConfguraion.bl.password,
                  clientId: itemData?.rewardOperatorConfguraion.bl.clientId,
                  grantType: itemData?.rewardOperatorConfguraion.bl.grantType,
                  scope: itemData?.rewardOperatorConfguraion.bl.scope,
                  clientSecret: itemData?.rewardOperatorConfguraion.bl.clientSecret,
                  senderMsisdn: itemData?.rewardOperatorConfguraion.bl.senderMsisdn,
                },
                itemData?.rewardOperatorConfguraion.gp.isUse && {
                  systemOperatorId: itemData?.rewardOperatorConfguraion.gp.systemOperatorId,
                  clientId: itemData?.rewardOperatorConfguraion.gp.clientId,
                  clientSecret: itemData?.rewardOperatorConfguraion.gp.clientSecret,
                  grantType: itemData?.rewardOperatorConfguraion.gp.grantType,
                },
              ].filter((item) => typeof item !== "boolean")
            : [],
        };
        UserService.createOwnerUser(formData).then((response) => {
          if (response.isSuccess) {
            openSuccessDialog("Success", `${itemData.companyName} user created successfully, ready to use`);
            setIsLoading(false);
            navigate("/config/users");
          } else {
            setSubmitting(false);
            setIsLoading(false);
          }
        });
      });
  };

  return (
    <Grid container justifyContent={"center"} spacing={2}>
      <Grid item lg={11} md={12} xs={12}>
        <Typography sx={{ ...theme.typography.pageheader }}>Create Parent User</Typography>
      </Grid>
      <Grid item xs={12}>
        <Formik
          initialValues={INITIAL_FORM_STATE}
          validationSchema={FORM_VALIDATION}
          onSubmit={(values: UserAddValues, { setSubmitting }) => {
            createUser(values, setSubmitting);
          }}
        >
          {({ values, dirty, isSubmitting, isValid, errors, setFieldValue, setErrors }) => (
            <Form>
              <Grid container>
                <Grid item xs={12}>
                  <Box sx={{ width: "100%" }}>
                    <>
                      <Stepper activeStep={activeStep} alternativeLabel>
                        {steps.map((label, index) => {
                          const stepProps: { completed?: boolean } = {};
                          const labelProps: {
                            optional?: React.ReactNode;
                          } = {};
                          return (
                            <Step key={label} {...stepProps}>
                              <StepLabel {...labelProps}>{label}</StepLabel>
                            </Step>
                          );
                        })}
                      </Stepper>
                      {activeStep === steps.length + 1 ? (
                        <React.Fragment>
                          <Typography sx={{ mt: 2, mb: 1 }}>All steps completed - you&apos;re finished</Typography>
                          <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                            <Box sx={{ flex: "1 1 auto" }} />
                            <Button onClick={handleReset}>Reset</Button>
                          </Box>
                        </React.Fragment>
                      ) : (
                        <SwipeableViews axis={theme.direction === "rtl" ? "x-reverse" : "x"} index={activeStep}>
                          <TabPanel value={activeStep} index={0} dir={theme.direction}>
                            <UserDetails dataRateCards={dataRateCards} smsRateCards={smsRateCards} values={values} handleNext={handleNext} />
                          </TabPanel>
                          <TabPanel value={activeStep} index={1} dir={theme.direction}>
                            <OperatorConfiguration
                              values={values}
                              handleBack={handleBack}
                              setFieldValue={setFieldValue}
                              dirty={dirty}
                              isSubmitting={isSubmitting}
                              isValid={isValid}
                            />
                          </TabPanel>
                        </SwipeableViews>
                      )}
                    </>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Grid>
    </Grid>
  );
}

export default CreateParentUser;
